import { useMemo } from 'react';
import { HoaDonDichVuTraLaiGiamGiaSearchProvider, useHoaDonDichVuTraLaiGiamGiaSearchContext } from './context';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ConfirmationDialogV2, ErpDataTableV2, ErpInputTableV2 } from '@/components/custom/erp';
import { ActionBar, FormDialog, SearchDialog } from './components';
import { detailPreviewColumns } from './cols-definition';
import { useSearchDialog, useTableData } from './hooks';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

const PREVIEW_ACTION_ORDER = ['export', 'pin'] as const;

function HoaDonBanDichVuPageContent() {
  const { handleSearchChange, searchState } = useHoaDonDichVuTraLaiGiamGiaSearchContext();

  const { showSearchDialog, handleSearch, handleSearchSubmit, closeSearchDialog } = useSearchDialog();
  const {
    data: hoaDonBanDichVus,
    isLoading,
    currentPage,
    totalItems,
    detail,
    fetchDetail,
    handlePageChange,
    addItem: addHoaDonBanDichVu,
    updateItem: updateHoaDonBanDichVu,
    deleteItem: deleteHoaDonBanDichVu,
    refreshData: refreshHoaDonBanDichVus
  } = useCRUD({
    endpoint: QUERY_KEYS.HOA_DON_DICH_VU_TRA_LAI_GIAM_GIA
  });
  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    handleRowClick,
    clearSelection,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useForms(fetchDetail);
  const hasFilterParams = searchState.filterParams && Object.keys(searchState.filterParams).length > 0;
  const displayData = hasFilterParams ? searchState.searchResults : hoaDonBanDichVus;

  const { tables } = useTableData(displayData, handleViewClick);
  const previewActions = useMemo(
    () => ({
      export: () => {},
      pin: () => {}
    }),
    []
  );

  const handleSubmit = async (data: BanHangPayload) => {
    try {
      if (formMode === 'add') {
        await addHoaDonBanDichVu(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateHoaDonBanDichVu(selectedObj.uuid, data);
      }
      clearSelection();
      handleCloseForm();
      refreshHoaDonBanDichVus();
    } catch (error) {
      return;
    }
  };

  return (
    <div className='flex size-full min-h-[calc(100vh-120px)] flex-col overflow-hidden'>
      {!showForm && (
        <>
          <ActionBar
            onAdd={handleAddClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onCopy={handleCopyClick}
            onSearch={handleSearch}
            onRefresh={async () => {
              await refreshHoaDonBanDichVus();
              if (selectedObj) await fetchDetail(selectedObj.uuid);
            }}
            isEditDisabled={!selectedObj}
          />

          <ResizablePanelGroup direction='vertical' className='flex-1 overflow-hidden'>
            <ResizablePanel defaultSize={55} minSize={20}>
              <div data-perf-id='hddv-tra-lai-giam-gia-master-table' className='h-full w-full overflow-hidden'>
                <ErpDataTableV2
                  tables={tables}
                  onRowClick={handleRowClick}
                  selectedRowId={selectedRowIndex || undefined}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onSearchChange={handleSearchChange}
                  loading={isLoading}
                />
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={45} minSize={20}>
              <div data-perf-id='hddv-tra-lai-giam-gia-bottom-detail-table' className='h-full overflow-hidden'>
                <ErpInputTableV2
                  rows={selectedObj ? detail?.chi_tiet || [] : []}
                  columns={detailPreviewColumns}
                  showIndexColumn
                  actions={previewActions}
                  actionOrder={PREVIEW_ACTION_ORDER as BanHangRecord}
                  actionsDisabled={formMode === 'view'}
                  loading={isLoading}
                  height='100%'
                />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </>
      )}

      {showForm && (
        <FormDialog
          formMode={formMode}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onClose={handleCloseForm}
          onSubmit={handleSubmit}
        />
      )}

      {showDelete && (
        <ConfirmationDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={async () => {
            if (selectedObj?.uuid) {
              await deleteHoaDonBanDichVu(selectedObj.uuid);
            }
            clearSelection();
            handleCloseDelete();
          }}
          title='Xác nhận xóa'
          message={`Bạn có chắc chắn muốn xóa hóa đơn ${selectedObj?.so_ct || ''}?`}
        />
      )}

      {showSearchDialog && (
        <SearchDialog open={showSearchDialog} onClose={closeSearchDialog} onSearch={handleSearchSubmit} />
      )}
    </div>
  );
}

export default function HoaDonBanDichVuPage() {
  return (
    <HoaDonDichVuTraLaiGiamGiaSearchProvider>
      <HoaDonBanDichVuPageContent />
    </HoaDonDichVuTraLaiGiamGiaSearchProvider>
  );
}
